import type {
  DeleteAccountDto,
  PricingItem,
  ProfileResponse,
  UpdateCompanyDto,
  UpdateDueDiligenceDto,
  UpdateProfileDto,
} from '@nestjs-boilerplate/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { apiClient } from '@/lib/api-client';

// Re-export types for consumers
export type { PricingItem, ProfileResponse } from '@nestjs-boilerplate/types';

// ============================================================================
// API Functions
// ============================================================================

export async function getProfile(): Promise<ProfileResponse> {
  return apiClient.fetch<ProfileResponse>('/settings/profile');
}

export async function updateProfile(data: UpdateProfileDto): Promise<void> {
  return apiClient.fetch<void>('/settings/profile', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function updateCompany(data: UpdateCompanyDto): Promise<void> {
  return apiClient.fetch<void>('/settings/company', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function updateDueDiligence(data: UpdateDueDiligenceDto): Promise<void> {
  return apiClient.fetch<void>('/settings/due-diligence', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteAccount(data: DeleteAccountDto): Promise<void> {
  return apiClient.fetch<void>('/settings/account', {
    method: 'DELETE',
    body: JSON.stringify(data),
  });
}

export async function getPricing(): Promise<PricingItem[]> {
  const response = await apiClient.fetch<{ items: PricingItem[] }>('/pricing');
  return response.items;
}

export async function changePasswordRequest(): Promise<{ redirectUrl: string }> {
  return apiClient.fetch<{ redirectUrl: string }>('/auth/change-password-request', {
    method: 'POST',
  });
}

// ============================================================================
// Query Keys
// ============================================================================

export const settingsKeys = {
  all: ['settings'] as const,
  profile: () => [...settingsKeys.all, 'profile'] as const,
  pricing: () => [...settingsKeys.all, 'pricing'] as const,
};

// ============================================================================
// React Query Hooks
// ============================================================================

export function useProfile() {
  return useQuery({
    queryKey: settingsKeys.profile(),
    queryFn: getProfile,
  });
}

export function usePricing() {
  return useQuery({
    queryKey: settingsKeys.pricing(),
    queryFn: getPricing,
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.profile() });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update profile');
    },
  });
}

export function useUpdateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.profile() });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update company information');
    },
  });
}

export function useUpdateDueDiligence() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateDueDiligence,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.profile() });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update due diligence settings');
    },
  });
}

export function useDeleteAccount() {
  return useMutation({
    mutationFn: deleteAccount,
    onSuccess: () => {
      toast.success('Account deletion scheduled. You will be logged out.');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete account');
    },
  });
}

export function useChangePasswordRequest() {
  return useMutation({
    mutationFn: changePasswordRequest,
    onSuccess: (data) => {
      // Redirect to password reset page with token
      window.location.href = data.redirectUrl;
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to request password change');
    },
  });
}
